import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, Save, Trash2 } from "lucide-react";
import { Providers, UNITS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const emptyForm = { name: "", unit_preferences: [], max_patients: 20, roles: ["psych_rounder"], fallback_provider_id: null };

const ROLE_OPTIONS = [
  { key: "psych_rounder", label: "Psych Rounder", color: "#0284c7" },
  { key: "medical_rounder", label: "Medical Rounder", color: "#6b4f8e" },
];

export default function ProviderRoster({ providers, onChange }) {
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      unit_preferences: [...p.unit_preferences],
      max_patients: p.max_patients,
      roles: p.roles && p.roles.length ? [...p.roles] : ["psych_rounder"],
      fallback_provider_id: p.fallback_provider_id || null,
    });
  };

  const reset = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const toggleUnit = (u) => {
    setForm((f) => ({
      ...f,
      unit_preferences: f.unit_preferences.includes(u)
        ? f.unit_preferences.filter((x) => x !== u)
        : [...f.unit_preferences, u],
    }));
  };

  const toggleRole = (r) => {
    setForm((f) => {
      const has = f.roles.includes(r);
      let next;
      if (has) {
        next = f.roles.filter((x) => x !== r);
        if (next.length === 0) next = [r === "psych_rounder" ? "medical_rounder" : "psych_rounder"];
      } else {
        next = [...f.roles, r];
      }
      return { ...f, roles: next };
    });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    try {
      if (editingId) {
        await Providers.update(editingId, form);
        toast.success("Provider updated.");
      } else {
        await Providers.create(form);
        toast.success("Provider added.");
      }
      reset();
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this provider from the roster?")) return;
    try {
      await Providers.remove(id);
      toast.success("Removed.");
      if (editingId === id) reset();
      onChange && (await onChange());
    } catch (e) {
      toast.error("Failed to remove.");
    }
  };

  return (
    <div className="space-y-5" data-testid="provider-roster">
      {/* Form */}
      <div className="border border-[#e2e8f0] rounded-lg p-4 bg-[#f8fafc]">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
          <div>
            <Label className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
              Name
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Dr. Adaora Okafor"
              className="mt-1 border-[#e2e8f0] focus-visible:ring-[#0284c7]"
              data-testid="provider-name-input"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
              Cap
            </Label>
            <Input
              type="number"
              min={1}
              value={form.max_patients ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  // Let the field be temporarily empty while the user re-types.
                  // We store null so it doesn't snap to 20 mid-edit — the save
                  // handler validates below.
                  setForm({ ...form, max_patients: null });
                  return;
                }
                const n = parseInt(raw, 10);
                if (Number.isFinite(n) && n >= 1) {
                  setForm({ ...form, max_patients: n });
                }
              }}
              onBlur={() => {
                // On blur, if the field is empty or invalid, restore a sensible
                // default (20) only then — never mid-typing.
                if (!Number.isFinite(form.max_patients) || form.max_patients < 1) {
                  setForm((f) => ({ ...f, max_patients: 20 }));
                }
              }}
              className="mt-1 border-[#e2e8f0] focus-visible:ring-[#0284c7]"
              data-testid="provider-cap-input"
            />
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
            Roles
          </Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {ROLE_OPTIONS.map((r) => {
              const on = form.roles.includes(r.key);
              return (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`role-toggle-${r.key}`}
                  onClick={() => toggleRole(r.key)}
                  className={[
                    "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                    on
                      ? "text-white border-transparent"
                      : "bg-white text-[#0f172a] border-[#e2e8f0] hover:border-[#0284c7]",
                  ].join(" ")}
                  style={on ? { backgroundColor: r.color, borderColor: r.color } : {}}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
            Unit preferences
          </Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {UNITS.map((u) => {
              const on = form.unit_preferences.includes(u);
              return (
                <button
                  key={u}
                  type="button"
                  data-testid={`unit-pref-toggle-${u}`}
                  onClick={() => toggleUnit(u)}
                  className={[
                    "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                    on
                      ? "bg-[#0284c7] text-white border-[#0284c7]"
                      : "bg-white text-[#0f172a] border-[#e2e8f0] hover:border-[#0284c7]",
                  ].join(" ")}
                >
                  Unit {u}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
            Fallback provider
          </Label>
          <p className="text-xs text-[#475569] mt-1 mb-2">
            If this provider isn&apos;t on duty, their carry-over patients hand off to this provider automatically.
          </p>
          <select
            value={form.fallback_provider_id || ""}
            onChange={(e) => setForm({ ...form, fallback_provider_id: e.target.value || null })}
            className="w-full h-9 px-3 rounded-md border border-[#e2e8f0] bg-white text-sm text-[#0f172a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0284c7]/40"
            data-testid="provider-fallback-select"
          >
            <option value="">— None —</option>
            {providers
              .filter((p) => p.id !== editingId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </div>

        <div className="mt-4 flex items-center gap-2 justify-end">
          {editingId && (
            <Button
              variant="ghost"
              onClick={reset}
              className="text-[#475569] hover:bg-[#f1f5f9]"
              data-testid="cancel-edit-provider-button"
            >
              <X className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
          )}
          <Button
            onClick={save}
            className="bg-[#0284c7] hover:bg-[#21473c] text-white"
            data-testid="save-provider-button"
          >
            {editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {editingId ? "Save changes" : "Add provider"}
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2 max-h-[320px] overflow-auto pr-1" data-testid="provider-list">
        {providers.length === 0 ? (
          <p className="text-sm text-[#475569] italic">No providers yet.</p>
        ) : (
          providers.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between p-3 border border-[#e2e8f0] rounded-lg bg-white hover:bg-[#f8fafc] transition-colors"
              data-testid={`provider-row-${p.id}`}
            >
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-[#475569] mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="flex gap-1">
                    {(p.roles && p.roles.length ? p.roles : ["psych_rounder"]).map((r) => {
                      const meta = ROLE_OPTIONS.find((o) => o.key === r);
                      if (!meta) return null;
                      return (
                        <span
                          key={r}
                          className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white"
                          style={{ backgroundColor: meta.color }}
                        >
                          {meta.label.replace(" Rounder", "")}
                        </span>
                      );
                    })}
                  </span>
                  <span>
                    {p.unit_preferences.length
                      ? `Prefers ${p.unit_preferences.join(", ")}`
                      : "No unit preference"}
                    {" · "}Cap {p.max_patients}
                    {p.fallback_provider_id && providers.find((x) => x.id === p.fallback_provider_id) && (
                      <> {" · "}Fallback → {providers.find((x) => x.id === p.fallback_provider_id).name}</>
                    )}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => startEdit(p)}
                  className="text-[#0284c7] hover:bg-[#e9f2ee]"
                  data-testid={`edit-provider-${p.id}`}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(p.id)}
                  className="text-[#b94a48] hover:bg-[#fbebe7]"
                  data-testid={`delete-provider-${p.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
